import React, { useEffect, useState } from "react";
import {
    FileText, X, AlertCircle, Download, User, Star, MessageCircle, Loader2, Eye
} from "lucide-react";
import { Card } from "./card";
import { Button } from "./Button";
import { Badge } from "./badge";
import { useNavigate } from "react-router-dom";
import { calculateEmergencyCost } from "../../lib/emergencyCostUtils";
import { GlobalService } from "../../modules/shared/services/global.service";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { updateOffer, fetchUserOffers } from "../../redux/Slices/offerSlice";
import { OfferStatusEnum } from "../../modules/shared/model/offer.interfaces";
import { createConversation, createChat } from "../../redux/Actions/AuthAction";
import { fetchParticipantChats, updateChat } from "../../redux/Actions/ChatActions";
import { ConversationTypeEnum } from "../../modules/shared/model/conversationType.enum";
import { ContextEnum } from "../../modules/shared/model/context.enum";
import { useWebSocket } from "../../hooks/useWebSocket";
import ReviewModal from "./ReviewModal";
import { useTranslation } from "react-i18next";

interface ClientOfferDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    offer: any;
    categoryFormSettings: any;
    showActions?: boolean;
    onOfferUpdated?: () => void;
    currentPage?: number;
    pageSize?: number;
    selectedTab?: string;
    searchTerm?: string;
    filters?: any;
    userId?: string;
}

const ClientOfferDetailsModal: React.FC<ClientOfferDetailsModalProps> = ({
    isOpen,
    onClose,
    offer,
    categoryFormSettings,
    showActions = true,
    onOfferUpdated,
    currentPage = 1,
    pageSize = 10,
    selectedTab = "received",
    searchTerm = "",
    filters = {},
    userId
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const { sendMessage: sendWebSocketMessage, isConnected } = useWebSocket();
    const [messageData, setMessageData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingAction, setLoadingAction] = useState<string | null>(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    if (!isOpen || !offer) return null;

    const isInternational = offer.type === "international";
    const isPending = offer.offer?.status === 2;

    // Helper function to check if file is an image
    const isImage = (file: any) => {
        const imageTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
        return imageTypes.includes(file.type?.toLowerCase()) ||
            file.name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|svg)$/);
    };

    // Helper function to check if file is a video
    const isVideo = (file: any) => {
        const videoTypes = ["video/mp4", "video/avi", "video/mov", "video/wmv", "video/flv", "video/webm"];
        return videoTypes.includes(file.type?.toLowerCase()) ||
            file.name?.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm)$/);
    };

    // Helper function to get file extension
    const getFileExtension = (filename: string) => {
        return filename?.split(".").pop()?.toLowerCase() || "";
    };

    // Helper function to download file
    const handleDownload = async (file: any) => {
        try {
            if (file.url || file.path) {
                const response = await fetch(file.url || file.path);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = file.name || `file-${file.id}.${getFileExtension(file.name)}`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                // Fallback for files without direct URL
                const a = document.createElement("a");
                a.href = `data:${file.type || "application/octet-stream"};base64,${file.content || ""}`;
                a.download = file.name || `file-${file.id}.${getFileExtension(file.name)}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        } catch (error) {
            console.error("Download failed:", error);
            alert("Download failed. Please try again.");
        }
    };

    // Function to create chat and send conversation message
    const createChatAndSendMessage = async (conversationType: ConversationTypeEnum, message: string) => {
        try {
            console.log("createChatAndSendMessage called with:", { conversationType, message, offer });

            const origionalOffer = offer?.origionalOffer;
            // Get client and professional IDs from offer
            const clientId = origionalOffer?.job?.userId?.id;
            const professionalId = origionalOffer?.user?.id;
            console.log("Extracted IDs:", { clientId, professionalId });

            if (!clientId || !professionalId) {
                console.warn("Missing client or professional ID in offer, skipping conversation message");
                console.log("Offer structure:", offer);
                return;
            }

            // Check if chat already exists between client and professional
            let existingChat = null;
            try {
                existingChat = await dispatch(fetchParticipantChats({ currentUserId: clientId, page: 1, limit: 50 })).unwrap();
            } catch (error) {
                // No existing chat found, will create new one
            }

            let chatResponse;
            if (existingChat.chats && existingChat.chats.length > 0) {
                const filteredChats = existingChat.chats.filter((chat: any) => {
                    const participantIds = chat.chat.participants.map((p: any) => p.id);
                    return participantIds.includes(clientId) && participantIds.includes(professionalId);
                });
                const chat = filteredChats[0].chat;
                chatResponse = chat;

                // Update the chat with lastMessage and lastMessageAt
                if (chatResponse && chatResponse.id) {
                    const updatedChat = {
                        lastMessage: message,
                        lastMessageAt: new Date().toISOString(),
                        unreadCount: { [clientId]: user?.id === professionalId ? 1 : 0, [professionalId]: user?.id === clientId ? 1 : 0 },
                    };
                    await dispatch(updateChat({
                        chatId: chatResponse.id,
                        data: updatedChat
                    }));
                }
            } else {
                // Create new chat
                const chatPayload = {
                    unreadCount: { [clientId]: user?.id === professionalId ? 0 : 1, [professionalId]: user?.id === clientId ? 0 : 1 },
                    participants: [
                        { id: clientId },
                        { id: professionalId }
                    ],
                    contextId: offer?.job?.id,
                    contextType: { id: ContextEnum.Message },
                    lastMessageAt: new Date().toISOString(),
                    lastMessage: message,
                };

                chatResponse = await dispatch(createChat({ data: chatPayload, isShowLoader: false })).unwrap();
            }

            // Send conversation message
            const conversationPayload = {
                isRead: false,
                message: message,
                sender: { id: user?.id },
                chatId: { id: chatResponse.id },
                type: { id: conversationType },
                referenceId: offer?.id,
                attachments: []
            };

            console.log("Creating conversation with payload:", conversationPayload);
            let conversation;
            try {
                conversation = await dispatch(createConversation({ data: conversationPayload, isShowLoader: false })).unwrap();
                console.log("conversation created successfully:", conversation);
            } catch (conversationError) {
                console.error("Error creating conversation:", conversationError);
                throw conversationError;
            }

            // Send WebSocket message to notify other participants (non-blocking)
            setTimeout(async () => {
                try {
                    if (sendWebSocketMessage && isConnected) {
                        const receivers = chatResponse.participants.map((p: any) => p.id).filter((id: any) => id !== user?.id);
                        if (receivers.length > 0) {
                            const messageData = {
                                receivers,
                                sender: user?.id || "",
                                type: "new_conversation",
                                detail: {
                                    conversationId: conversation.id,
                                    conversation: conversation,
                                    chatId: conversation.chatId.id,
                                    message: conversationPayload.message,
                                    senderId: conversation.sender.id,
                                    attachments: conversationPayload.attachments,
                                    type: conversationPayload.type.id,
                                    referenceId: conversationPayload.referenceId,
                                    timestamp: new Date().toISOString()
                                },
                                conversationId: conversationPayload.chatId.id
                            };
                            setMessageData(messageData);
                            await sendWebSocketMessage(messageData);
                        }
                    }
                } catch (wsError) {
                    // Don't fail the entire operation if WebSocket fails
                }
            }, 100);
        } catch (error) {
            console.error("Error creating chat or sending message:", error);
            // Don't throw error here as the main action should still proceed
        }
    };

    useEffect(() => {
        if (messageData) {
            onOfferUpdated?.();
        }
    }, [messageData]);

    // Handle offer acceptance
    const handleAcceptOffer = async () => {
        setIsLoading(true);
        setLoadingAction("accepting");

        try {
            // Update offer status to accepted (status ID: 3)
            await dispatch(updateOffer({
                id: offer.id,
                data: {
                    status: { id: OfferStatusEnum.Accepted },
                }
            })).unwrap();

            // Send conversation message
            await createChatAndSendMessage(
                ConversationTypeEnum.Quote_Accepted,
                t("clientOfferDetailsModal.quoteAccepted")
            );

            // Refresh offers
            if (userId) {
                dispatch(fetchUserOffers({
                    page: currentPage,
                    limit: pageSize,
                    jobUserId: userId,
                    isSent: selectedTab === "sent",
                    search: searchTerm || undefined,
                    status: filters.status !== "all" ? (filters.status === "approved" ? 2 : filters.status === "accepted" ? 3 : filters.status === "rejected" ? 4 : filters.status === "expired" ? 5 : filters.status === "cancelled" ? 6 : filters.status === "completed" ? 7 : filters.status === "archived" ? 8 : filters.status === "deleted" ? 9 : undefined) : undefined,
                    statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
                    sort: [{ orderBy: "createdAt", order: "DESC" }]
                }));
            }

            GlobalService.showToast("success", t("common.success"), t("clientOfferDetailsModal.offerAcceptedSuccess"));
            // onOfferUpdated?.();
            setTimeout(() => {
                onClose();
            }, 500);
        } catch (error) {
            GlobalService.showToast("error", t("common.error"), t("clientOfferDetailsModal.offerAcceptedError"));
        } finally {
            setIsLoading(false);
            setLoadingAction(null);
        }
    };

    // Handle offer decline
    const handleDeclineOffer = async () => {
        setIsLoading(true);
        setLoadingAction("declining");

        try {
            // Update offer status to rejected (status ID: 4)
            await dispatch(updateOffer({
                id: offer.id,
                data: {
                    status: { id: OfferStatusEnum.Rejected },
                    timelineType: { id: Number(offer.offer.timelineType.id) },
                    files: offer.offer.files ? offer.offer.files : [],
                    user: { id: offer.professional.id },
                    job: { id: offer.offer?.job?.id },
                }
            })).unwrap();

            // Send conversation message
            await createChatAndSendMessage(
                ConversationTypeEnum.Quote_Rejected,
                t("clientOfferDetailsModal.quoteRejected")
            );

            // Refresh offers
            if (userId) {
                dispatch(fetchUserOffers({
                    page: currentPage,
                    limit: pageSize,
                    jobUserId: userId,
                    isSent: selectedTab === "sent",
                    search: searchTerm || undefined,
                    status: filters.status !== "all" ? (filters.status === "approved" ? 2 : filters.status === "accepted" ? 3 : filters.status === "rejected" ? 4 : filters.status === "expired" ? 5 : filters.status === "cancelled" ? 6 : filters.status === "completed" ? 7 : filters.status === "archived" ? 8 : filters.status === "deleted" ? 9 : undefined) : undefined,
                    statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
                    sort: [{ orderBy: "createdAt", order: "DESC" }]
                }));
            }

            GlobalService.showToast("success", t("common.success"), t("clientOfferDetailsModal.offerDeclinedSuccess"));
            // onOfferUpdated?.();
            setTimeout(() => {
                onClose();
            }, 500);
        } catch (error) {
            GlobalService.showToast("error", t("common.error"), t("clientOfferDetailsModal.offerDeclinedError"));
        } finally {
            setIsLoading(false);
            setLoadingAction(null);
        }
    };

    return (
        <>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
                {/* Loading Overlay */}
                {isLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                            <p className="text-sm text-gray-600 capitalize">
                                {loadingAction} offer...
                            </p>
                        </div>
                    </div>
                )}

                <div className="flex justify-between items-start p-6 border-b">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900">{offer.jobTitle}</h3>
                        <p className="text-sm text-gray-600">{t("clientOfferDetailsModal.offerIdLabel")} {offer.id}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6">
                    {/* Offer Type Badge */}
                    <div className="mb-6">
                        <Badge variant={undefined} className={`
              ${offer.type === "international" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}
            `}>
                            {offer.type === "international" ? t("clientOfferDetailsModal.international") : t("clientOfferDetailsModal.local")}
                        </Badge>
                    </div>

                    {/* Professional Information */}
                    {offer.professional && (
                        <div className="mb-6">
                            <h4 className="text-lg font-medium text-gray-900 mb-3">{t("clientOfferDetailsModal.professionalInformation")}</h4>
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center gap-4">
                                    <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                                        {(() => {
                                            const p = offer.professional as any;
                                            const photoUrl = offer?.origionalOffer?.user?.photo?.path || "";
                                            return photoUrl ? (
                                                <img src={photoUrl} alt={p?.name || "Professional"} className="h-16 w-16 object-cover" />
                                            ) : (
                                                <User className="h-8 w-8 text-gray-600" />
                                            );
                                        })()}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="font-medium text-lg">{offer.professional.name}</span>
                                            {offer.professional.verified && (
                                                <Badge variant={undefined} className="bg-green-50 text-green-700">{t("clientOfferDetailsModal.verified")}</Badge>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                                            <div>
                                                <span className="font-medium">{t("clientOfferDetailsModal.rating")}</span>
                                                <div className="flex items-center gap-1">
                                                    <Star className="h-4 w-4 text-yellow-400 fill-current" />
                                                    <span>{offer.origionalOffer.user.averageRating}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <button
                                                    onClick={() => setShowReviewModal(true)}
                                                    className="text-left hover:bg-blue-50 p-2 sm:p-3 rounded-md transition-all duration-200 cursor-pointer border border-transparent hover:border-blue-200 group flex items-center gap-2 w-full sm:w-auto"
                                                >
                                                    <Eye className="h-4 w-4 text-gray-500 group-hover:text-blue-600 flex-shrink-0" />
                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1 min-w-0">
                                                        <span className="font-medium text-sm sm:text-base">{t("clientOfferDetailsModal.reviews")}</span>
                                                        <span className="text-blue-600 hover:text-blue-800 group-hover:underline text-sm sm:text-base truncate">
                                                            {offer.origionalOffer.user.reviewsCount} {t("clientOfferDetailsModal.reviewsText")}
                                                        </span>
                                                    </div>
                                                </button>
                                            </div>
                                            <div>
                                                <span className="font-medium">{t("clientOfferDetailsModal.jobsCompleted")}</span>
                                                <span>{offer.professional.completedJobs}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium">{t("clientOfferDetailsModal.serviceAreas")}</span>
                                                <span>
                                                    {offer?.origionalOffer?.serviceAreas && offer.origionalOffer.serviceAreas.length > 0
                                                        ? offer.origionalOffer.serviceAreas
                                                            .slice(0, 2)
                                                            .map((a: any) => (a?.province ? `${a.province}, ${a?.country?.name || ""}` : (a?.country?.name || "")))
                                                            .filter(Boolean)
                                                            .join(" | ")
                                                        : (offer.professional.location || t("clientOfferDetailsModal.unknown"))}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Offer Details */}
                    <div className="mb-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">{t("clientOfferDetailsModal.offerDetails")}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <span className="text-sm font-medium text-gray-700">{t("clientOfferDetailsModal.budget")}</span>
                                    <div className="text-2xl font-bold text-gray-900">
                                        {offer.offer.currency} {offer.offer.amount.toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700">{t("clientOfferDetailsModal.timeline")}</span>
                                    <div className="text-lg font-medium">{offer.offer.timeline}</div>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700">{t("clientOfferDetailsModal.status")}</span>
                                    <Badge variant={undefined} className={`
                    ${offer.offer.status === 3 ? "bg-green-100 text-green-800" :
                                            offer.offer.status === 4 ? "bg-red-100 text-red-800" :
                                                offer.offer.status === 10 ? "bg-red-900 text-white" :
                                                    "bg-gray-100 text-gray-800"}
                  `}>
                                        {offer.offer.status === 2 ? t("clientOfferDetailsModal.approved")
                                            : offer.offer.status === 3 ? t("clientOfferDetailsModal.accepted")
                                                : offer.offer.status === 4 ? t("clientOfferDetailsModal.rejected")
                                                    : offer.offer.status === 5 ? t("clientOfferDetailsModal.expired")
                                                        : offer.offer.status === 6 ? t("clientOfferDetailsModal.cancelled")
                                                            : offer.offer.status === 7 ? t("clientOfferDetailsModal.completed")
                                                                : offer.offer.status === 8 ? t("clientOfferDetailsModal.archived")
                                                                    : offer.offer.status === 9 ? t("clientOfferDetailsModal.deleted")
                                                                        : offer.offer.status === 10 ? t("clientOfferDetailsModal.permanentlyDeleted")
                                                                            : offer.offer.status === 11 ? t("clientOfferDetailsModal.suspended")
                                                                                : offer.offer.status === 12 ? t("clientOfferDetailsModal.rejected")
                                                                                    : offer.offer.status === 13 ? t("clientOfferDetailsModal.adminDeleted")
                                                                                        : offer.offer.status === 14 ? t("clientOfferDetailsModal.adminPermanentlyDeleted")
                                                                                            : t("clientOfferDetailsModal.unknown")
                                        }
                                    </Badge>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <span className="text-sm font-medium text-gray-700">{t("clientOfferDetailsModal.created")}</span>
                                    <div className="text-sm">{new Date(offer.offer.sentAt).toLocaleDateString()}</div>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700">{t("clientOfferDetailsModal.validUntil")}</span>
                                    <div className="text-sm">{new Date(offer.offer.validUntil).toLocaleDateString()}</div>
                                </div>
                                {isInternational && offer.offer.milestones && (
                                    <div>
                                        <span className="text-sm font-medium text-gray-700">{t("clientOfferDetailsModal.milestones")}</span>
                                        <div className="text-sm">{t("clientOfferDetailsModal.milestonesCount", { count: offer.offer.milestones.length })}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Emergency Exit Cost */}
                    {offer.origionalOffer?.job?.isEmergency && (
                        <div className="mb-6">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertCircle className="h-5 w-5 text-red-600" />
                                    <h4 className="text-lg font-medium text-red-800">{t("clientOfferDetailsModal.emergencyService")}</h4>
                                </div>
                                <p className="text-sm text-red-700">
                                    Emergency Service: Client agreed to pay ${calculateEmergencyCost(
                                        offer.origionalOffer.job?.emergencyTimeSlot,
                                        offer.origionalOffer.job?.emergencyDays,
                                        categoryFormSettings
                                    )} outgoing fee
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Cover Letter */}
                    <div className="mb-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">{t("clientOfferDetailsModal.coverLetter")}</h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="prose max-w-none">
                                {offer.coverLetter.split("\n").map((paragraph: string, index: number) => (
                                    <p key={index} className="mb-2 text-gray-700">{paragraph}</p>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Media Files Section */}
                    {offer.attachments && offer.attachments.length > 0 && (
                        <div className="mb-6">
                            <h4 className="text-lg font-medium text-gray-900 mb-4">{t("clientOfferDetailsModal.attachmentsAndMedia")}</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {offer.attachments.map((file: any, index: number) => (
                                    <div key={index} className="border rounded-lg overflow-hidden bg-gray-50">
                                        {isImage(file) ? (
                                            <div className="relative group">
                                                <img
                                                    src={file.url || file.path || `data:${file.type};base64,${file.content}`}
                                                    alt={file.name || `Image ${index + 1}`}
                                                    className="w-full h-32 object-cover"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCAxMDAgTDEwMCA2MCBMMTQwIDEwMEg2MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIwIDIwQzIwIDE3LjggMTcuOCAxNSAxNSAxNUMxMi4yIDE1IDEwIDE3LjIgMTAgMjBDMTAgMjIuOCAxMi4yIDI1IDE1IDI1QzE3LjggMjUgMjAgMjIuOCAyMCAyMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+Cjwvc3ZnPgo=";
                                                    }}
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                                                    <button
                                                        onClick={() => handleDownload(file)}
                                                        className="opacity-0 group-hover:opacity-100 bg-white text-gray-800 px-3 py-2 rounded-lg shadow-lg hover:bg-gray-100 transition-all duration-200 flex items-center gap-2"
                                                    >
                                                        <Download className="h-4 w-4" />
                                                        {t("clientOfferDetailsModal.download")}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : isVideo(file) ? (
                                            <div className="relative group">
                                                <video
                                                    src={file.url || file.path || `data:${file.type};base64,${file.content}`}
                                                    className="w-full h-32 object-cover"
                                                    controls
                                                    preload="metadata"
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                                                    <button
                                                        onClick={() => handleDownload(file)}
                                                        className="opacity-0 group-hover:opacity-100 bg-white text-gray-800 px-3 py-2 rounded-lg shadow-lg hover:bg-gray-100 transition-all duration-200 flex items-center gap-2"
                                                    >
                                                        <Download className="h-4 w-4" />
                                                        {t("clientOfferDetailsModal.download")}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="p-4 text-center">
                                                <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                                <p className="text-sm text-gray-600 truncate">{file.name || `File ${index + 1}`}</p>
                                                <p className="text-xs text-gray-500">{getFileExtension(file.name)}</p>
                                            </div>
                                        )}
                                        <div className="p-3 border-t">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium text-gray-700 truncate">
                                                    {file.name || `File ${index + 1}`}
                                                </span>
                                                <button
                                                    onClick={() => handleDownload(file)}
                                                    className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded"
                                                    title="Download file"
                                                >
                                                    <Download className="h-4 w-4" />
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {file.type || t("clientOfferDetailsModal.unknownType")} • {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : t("clientOfferDetailsModal.sizeUnknown")}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center gap-3 pt-4 border-t">
                        <div className="flex items-center gap-3">
                            {offer.offer.status != 3 && offer.offer.isSent && (
                                <span className="text-blue-500 flex items-center">
                                    {t("clientOfferDetailsModal.chatMessage")}
                                </span>
                            )}
                        </div>

                        <div className="flex items-center gap-3">
                            {showActions && offer.origionalOffer?.isSent === false && offer.offer.status !== 9 && offer.offer.status !== 10 && offer.offer.status !== 8 && offer.offer.status !== OfferStatusEnum.Admin_Suspended && offer.offer.status !== OfferStatusEnum.Admin_Deleted && (
                                <>
                                    {offer.offer.status !== 4 && (
                                        <Button
                                            variant="outline"
                                            onClick={handleDeclineOffer}
                                            disabled={isLoading}
                                            className="text-red-600 border-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            {loadingAction === "declining" && <Loader2 className="h-4 w-4 animate-spin" />}
                                            {loadingAction === "declining" ? t("clientOfferDetailsModal.declining") : t("clientOfferDetailsModal.declineOffer")}
                                        </Button>
                                    )}
                                    {offer.offer.status !== 3 && (
                                        <Button
                                            onClick={handleAcceptOffer}
                                            disabled={isLoading}
                                            className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            {loadingAction === "accepting" && <Loader2 className="h-4 w-4 animate-spin" />}
                                            {loadingAction === "accepting" ? t("clientOfferDetailsModal.accepting") : t("clientOfferDetailsModal.acceptOffer")}
                                        </Button>
                                    )}
                                </>
                            )}

                            {offer.offer.status === 3 && (
                                <Button
                                    onClick={() => {
                                        window.location.href = "/client/MessageAndVideoCall?professionalId=" + offer.offer.user.id + "&professionalName=" + encodeURIComponent(offer.offer.user.firstName);
                                        // navigate(`/client/MessageAndVideoCall?professionalId=${offer.offer.user.id}&professionalName=${encodeURIComponent(offer.offer.user.firstName)}`);
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                    <MessageCircle className="h-4 w-4 mr-2" />
                                    {t("clientOfferDetailsModal.messageProfessional")}
                                </Button>
                            )}

                            <Button variant="outline" onClick={onClose}>
                                {t("clientOfferDetailsModal.close")}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Review Modal */}
        <ReviewModal
            isOpen={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            professional={{
                ...offer?.origionalOffer?.user,
                reviewsList: Array.isArray(offer?.origionalOffer?.user?.reviewsList)
                    ? offer.origionalOffer.user.reviewsList
                    : (offer?.origionalOffer?.user?.reviews ? offer.origionalOffer.user.reviews : [])
            }}
        />
    </>
    );
};

export default ClientOfferDetailsModal;
